import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProjectProvider } from './contexts/ProjectContext';
import TeamsPage from './pages/Teams';
import FixturesPage from './pages/FixturesPage';
import Layout from './components/Layout';
import Login from './pages/Login';
import Standings from './pages/Standings';
import Signup from './pages/projects/:project/Signup';
import UserManagement from './pages/UserManagement';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/Forgot-Password';
import { supabase } from '@/lib/supabaseClient';

const App: React.FC = () => {
  useEffect(() => {
    const match = window.location.pathname.match(/^\/projects\/([^\/]+)/);
    const projectSlug = match?.[1];

    if (projectSlug) {
      const manifestHref = `/projects/${projectSlug}/manifest.json`;
      const appleIconHref = `/lovable-uploads/${projectSlug}-apple-touch-icon.png`;

      const existingManifest = document.querySelector("link[rel='manifest']");
      if (existingManifest) {
        existingManifest.setAttribute("href", manifestHref);
      } else {
        const link = document.createElement("link");
        link.rel = "manifest";
        link.href = manifestHref;
        document.head.appendChild(link);
      }

      const existingAppleIcon = document.querySelector("link[rel='apple-touch-icon']");
      if (existingAppleIcon) {
        existingAppleIcon.setAttribute("href", appleIconHref);
      } else {
        const appleLink = document.createElement("link");
        appleLink.rel = "apple-touch-icon";
        appleLink.href = appleIconHref;
        document.head.appendChild(appleLink);
      }
    }

    const shouldUpdateLastActive = () => {
      const lastUpdate = localStorage.getItem('lastActiveUpdate');
      if (!lastUpdate) return true;
      const hoursSince = (Date.now() - new Date(lastUpdate).getTime()) / (1000 * 60 * 60);
      return hoursSince > 12;
    };

    const updateLastActive = async () => {
      if (!shouldUpdateLastActive()) return;
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('users')
          .update({ last_active_at: new Date().toISOString() })
          .eq('id', user.id);
        localStorage.setItem('lastActiveUpdate', new Date().toISOString());
      }
    };

    updateLastActive();
  }, []);

  return (
    <AuthProvider>
      <ProjectProvider>
        <Router>
          <Routes>
            <Route path="/projects/:projectSlug/*" element={<Layout />}>
              <Route index element={<Navigate to="standings" replace />} />
              <Route path="standings" element={<Standings />} />
              <Route path="teams" element={<TeamsPage />} />
              <Route path="fixtures" element={<FixturesPage />} />
              <Route path="admin/usermanagement" element={<UserManagement />} />
            </Route>

            <Route path="/login" element={<Login />} />
            <Route path="/projects/:projectSlug/signup" element={<Signup />} />
            <Route path="/resetpassword" element={<ResetPassword />} />
            <Route path="/forgotpassword" element={<ForgotPassword />} />
            <Route
              path="*"
              element={
                <Navigate to="/projects/gwent/standings" replace />
              }
            />
          </Routes>
        </Router>
      </ProjectProvider>
    </AuthProvider>
  );
};

export default App;